import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi";

interface Draft {
  session_id: string;
  patient_id: string;
  format: "soap" | "dap" | "birp";
  generated_at: number;
  session_date?: string;
  title?: string;
}

interface Patient { patient_id: string; name: string }

const POLL_MS = 60_000;

/**
 * Header button: progress-note drafts written automatically after a session
 * (POST /sessions/{id}/complete) and waiting for the therapist to review and
 * sign. Picking one opens that patient + session with the session panel.
 */
export const NotesToReview: React.FC<{
  onOpen: (patientId: string, patientName: string, sessionId: string) => void;
}> = ({ onOpen }) => {
  const { t } = useTranslation();
  const api = useApi();
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        api.get<{ drafts: Draft[] }>("/notes/drafts"),
        api.get<{ items: Patient[] }>("/patients"),
      ]);
      setNames(Object.fromEntries(p.items.map((x) => [x.patient_id, x.name])));
      setDrafts(d.drafts);
      setError(false);
    } catch (e) {
      console.error("loading note drafts failed", e);
      setError(true);
    }
  }, [api]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!error && (!drafts || drafts.length === 0)) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="headerBtn"
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        title={t("notesReview.title")}
        aria-label={t("notesReview.title")}
        style={error ? { color: "var(--color-danger)" } : undefined}
      >
        <span aria-hidden>📝</span>
        <span style={{ fontWeight: 700, fontSize: 12 }}>{error ? "!" : drafts!.length}</span>
      </button>
      {open && (
        <div className="notesReview__menu">
          <div className="notesReview__head">{t("notesReview.title")}</div>
          {error ? (
            <div className="notesReview__error">{t("notesReview.loadFailed")}</div>
          ) : drafts!.map((d) => (
            <button
              key={`${d.session_id}-${d.format}`}
              className="notesReview__item"
              onClick={() => { setOpen(false); onOpen(d.patient_id, names[d.patient_id] ?? "", d.session_id); }}
            >
              <span className="notesReview__name">{names[d.patient_id] ?? t("notesReview.unknownPatient")}</span>
              <span className="notesReview__meta">{d.session_date ?? d.title ?? ""} · {d.format.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
      <style>{`
        .notesReview__menu {
          position: absolute; right: 0; top: calc(100% + 6px); z-index: var(--z-dropdown); min-width: 260px;
          background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-md);
          box-shadow: var(--shadow-lg); padding: 6px; display: flex; flex-direction: column;
        }
        .notesReview__head { font-size: 11px; font-weight: 700; letter-spacing: var(--tracking-wide); text-transform: uppercase;
          color: var(--color-muted); padding: 6px 10px; }
        .notesReview__item { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 8px 10px;
          border: none; background: transparent; border-radius: var(--radius-sm); cursor: pointer; text-align: left; }
        .notesReview__item:hover { background: var(--color-brand-ghost); }
        .notesReview__name { font-size: 14px; font-weight: 600; color: var(--color-ink); }
        .notesReview__meta { font-size: 12px; color: var(--color-muted); }
        .notesReview__error { font-size: 13px; color: var(--color-danger); padding: 8px 10px; }
      `}</style>
    </div>
  );
};
